import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { LayoutDashboard, BookOpen, LogOut, Menu, FileText, Play, PanelLeftClose, PanelLeft, CalendarCheck, UserCircle, Share2, Settings as SettingsIcon, BabyIcon, GraduationCap, Receipt, Salad, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { getInitials } from '../lib/utils';
import { useState } from 'react';
import SoulAIWidget from '../components/SoulAIWidget';
import { TierBadge, isEliteTier, eliteAvatarRing, eliteAvatarFallback } from '../components/TierBadge';
import SuspendedBanner from '../components/SuspendedBanner';
import PhoneVerifyGate from '../components/PhoneVerifyGate';

const navItems = [
  { to: '/learner/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/learner/learn', icon: GraduationCap, label: 'Learn' },
  { to: '/learner/soulai-coach', icon: Sparkles, label: 'SoulAI Coach', prenatalOnly: true, hideForTrial: true },
  { to: '/learner/book', icon: CalendarCheck, label: 'Book Classes' },
  { to: '/learner/batches', icon: BookOpen, label: 'My Batches' },
  { to: '/learner/vault', icon: FileText, label: 'Medical Vault' },
  { to: '/learner/nutritionist', icon: Salad, label: 'Talk to Expert' },
  { to: '/learner/baby-tracker', icon: BabyIcon, label: 'Baby Tracker', postnatalOnly: true },
  { to: '/learner/refer', icon: Share2, label: 'Refer a Friend' },
  { to: '/learner/profile', icon: UserCircle, label: 'My Profile' },
  { to: '/learner/billing', icon: Receipt, label: 'Billing' },
  { to: '/learner/settings', icon: SettingsIcon, label: 'Settings' },
];

function SidebarContent({ onClose, collapsed }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const tier = user?.learner_type;
  const isElite = isEliteTier(tier);
  // Display label: "Elite" for elite/gold (legacy), capitalised tier otherwise,
  // fallback to "Learner" if no tier on file.
  const tierLabel = isElite
    ? 'Elite'
    : tier === 'trial_paid' ? 'Paid Trial'
    : tier === 'free' ? 'Free'
    : 'Learner';
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 shrink-0 rounded-xl overflow-hidden bg-white flex items-center justify-center">
            <img src="/logo.png" alt="Soul Sanskar" className="h-14 w-14 object-contain scale-150" />
          </div>
          {!collapsed && (
            <div>
              <h1 className="font-heading text-base font-semibold tracking-tight leading-tight">Soul Sanskar</h1>
              <p className="text-[10px] text-muted-foreground">Academy</p>
            </div>
          )}
        </div>
      </div>
      <nav className="flex-1 p-2 space-y-0.5">
        {navItems
          .filter(item => !item.postnatalOnly || (user?.pregnancy_profile?.course_type === 'postnatal' || user?.pregnancy_profile?.stage === 'postnatal' || !!user?.pregnancy_profile?.delivery_date))
          .filter(item => !item.prenatalOnly || !(user?.pregnancy_profile?.course_type === 'postnatal' || user?.pregnancy_profile?.stage === 'postnatal' || !!user?.pregnancy_profile?.delivery_date))
          // Paid-trial learners found the Coach confusing on the web portal
          // (founder, 3 Oct 2026) — it appears once they enrol.
          .filter(item => !item.hideForTrial || tier !== 'trial_paid')
          .map(item => (
          <NavLink key={item.to} to={item.to} onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${isActive ? 'bg-primary/10 text-primary border-l-2 border-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`
            }
            title={collapsed ? item.label : undefined}>
            <item.icon className="h-4 w-4 shrink-0" />{!collapsed && item.label}
          </NavLink>
        ))}
      </nav>
      <div className={`p-3 border-t ${collapsed ? 'px-2' : ''}`}>
        {!collapsed && (
          <div className="flex items-center gap-3 mb-3">
            <Avatar className={`h-8 w-8 ${eliteAvatarRing(tier)}`}>
              <AvatarFallback className={`text-xs ${eliteAvatarFallback(tier, 'bg-primary/10 text-primary')}`}>{getInitials(user?.name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <TierBadge tier={tier} size="sm" data-testid="elite-member-badge" />
              </div>
              <p className="text-xs text-muted-foreground">{tierLabel}</p>
            </div>
          </div>
        )}
        <Button variant="ghost" size="sm" data-testid="sign-out-button" className={`w-full ${collapsed ? 'px-2' : 'justify-start'} text-muted-foreground`} onClick={() => { logout(); navigate('/login'); }}>
          <LogOut className="h-4 w-4 mr-2" />{!collapsed && 'Sign out'}
        </Button>
      </div>
    </div>
  );
}

export default function LearnerLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="flex h-screen bg-background">
      <aside className={`hidden lg:flex ${collapsed ? 'w-[68px]' : 'w-[248px]'} shrink-0 border-r bg-white/70 dark:bg-slate-900/70 backdrop-blur flex-col transition-all duration-300 relative`}>
        <SidebarContent onClose={() => {}} collapsed={collapsed} />
        <button onClick={() => setCollapsed(!collapsed)} className="absolute -right-3 top-20 z-10 h-6 w-6 rounded-full border bg-white dark:bg-slate-900 shadow-sm flex items-center justify-center hover:bg-muted transition-colors" data-testid="sidebar-collapse-button">
          {collapsed ? <PanelLeft className="h-3 w-3" /> : <PanelLeftClose className="h-3 w-3" />}
        </button>
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[248px]"><SidebarContent onClose={() => setMobileOpen(false)} collapsed={false} /></SheetContent>
      </Sheet>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b bg-white/60 dark:bg-slate-900/60 backdrop-blur flex items-center px-4 lg:px-6 gap-3">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)}><Menu className="h-5 w-5" /></Button>
          <div className="flex-1" />
        </header>
        {/* V14.5 — Non-blocking suspension banner. Auto-hides for active users. */}
        <SuspendedBanner />
        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
      {/* V20 — one-time phone verification before the portal (all login paths) */}
      <PhoneVerifyGate />
      <SoulAIWidget />
    </div>
  );
}
